/** @format */

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../../config/i18n";
import {
    Box,
    Container,
    CircularProgress,
    Typography,
    Grid,
    TextField,
    MenuItem,
    Button,
    Alert,
    Slider,
    InputAdornment,
    Chip,
    FormControl,
    InputLabel,
    Select,
    Checkbox,
    ListItemText,
    OutlinedInput,
} from "@mui/material";
import {
    PlayArrow,
    TuneOutlined,
    ArrowBack,
} from "@mui/icons-material";
import {
    SUPPORTED_COUNTRIES,
    getCountryByCode,
} from "../../utility/stripeConnectCountries";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch } from "../../redux/store/store";
import {
    createQuickDemoCircle,
    createCustomSandboxCircle,
} from "../../redux/actions/sandboxAction";
import {
    selectSandboxCircle,
    selectSandboxIsLoading,
    selectSandboxError,
} from "../../redux/selectors/sandboxSelector";
import { clearSandboxState } from "../../redux/slices/sandboxSlice";
import { ICreateSandboxCirclePayload } from "../../utility/types/sandboxTypes";
import { colors } from "../../utility/types/constant";
import {
    SandboxPageWrapper,
    SandboxHeroBanner,
    SandboxBadge,
    SandboxHeroTitle,
    SandboxHeroSubtitle,
    SandboxContentArea,
    SandboxSectionCard,
    SandboxSectionTitle,
    SandboxDisclaimer,
} from "./styles";
import SandboxDashboard from "./SandboxDashboard";
import { Info } from "@mui/icons-material";

type DemoMode = "idle" | "loading" | "ready" | "custom-form";

type SupportedLanguage = "en" | "es" | "fr" | "mx";

function getSupportedLanguage(language?: string): SupportedLanguage {
    const key = (language || "en").toLowerCase().split("-")[0];
    if (key === "es" || key === "fr" || key === "mx") return key;
    return "en";
}

// ── Helpers ───────────────────────────────────────────────────────────────
function nextMondayString(): string {
    const today = new Date();
    const day = today.getDay();
    const daysUntilMonday = day === 1 ? 7 : (8 - day) % 7;
    const monday = new Date(today);
    monday.setDate(today.getDate() + daysUntilMonday);
    return monday.toISOString().split("T")[0];
}

/** Maps the active i18n locale to a sensible default country + currency. */
function getLocaleDefaults(language?: string): { country: string; currency: string } {
    const map: Record<SupportedLanguage, { country: string; currency: string }> = {
        es: { country: "ES", currency: "eur" },
        fr: { country: "FR", currency: "eur" },
        mx: { country: "MX", currency: "mxn" },
        en: { country: "US", currency: "usd" },
    };
    return map[getSupportedLanguage(language)];
}

// ── Custom sandbox form ───────────────────────────────────────────────────
const ALL_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

interface CustomFormState {
    circleName: string;
    memberCount: number;
    contributionAmount: number;
    contributionFrequency: "Weekly" | "Bi-Weekly";
    country: string;
    currency: string;
    contributionDays: string[];
    startDate: string;
}

function CustomSandboxForm({
    onSubmit,
    onCancel,
    isLoading,
}: {
    onSubmit: (payload: ICreateSandboxCirclePayload) => void;
    onCancel: () => void;
    isLoading: boolean;
}) {
    const { t, i18n: i18nInstance } = useTranslation("sandbox");
    const currentLanguage = i18nInstance.resolvedLanguage || i18nInstance.language;
    const localeDefaults = getLocaleDefaults(currentLanguage);
    const [form, setForm] = useState<CustomFormState>({
        circleName: t("demo.defaultCircleName"),
        memberCount: 6,
        contributionAmount: 150,
        contributionFrequency: "Weekly",
        country: localeDefaults.country,
        currency: localeDefaults.currency,
        contributionDays: ["Monday"],
        startDate: nextMondayString(),
    });

    const [countryManuallySelected, setCountryManuallySelected] = useState(false);

    const [errors, setErrors] = useState<Partial<Record<keyof CustomFormState, string>>>({});

    const validate = (): boolean => {
        const e: Partial<Record<keyof CustomFormState, string>> = {};
        if (!form.circleName.trim()) e.circleName = t("demo.errorCircleName");
        if (form.memberCount < 2 || form.memberCount > 20)
            e.memberCount = t("demo.errorMemberCount");
        if (form.contributionAmount < 10 || form.contributionAmount > 10000)
            e.contributionAmount = t("demo.errorAmount");
        if (!form.startDate) e.startDate = t("demo.errorStartDate");
        if (form.contributionDays.length === 0)
            e.contributionDays = t("demo.errorDays");
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleCountryChange = (countryCode: string) => {
        const config = getCountryByCode(countryCode);
        setCountryManuallySelected(true);
        setForm((f) => ({
            ...f,
            country: countryCode,
            currency: config?.currency ?? "usd",
        }));
    };

    useEffect(() => {
        if (countryManuallySelected) return;
        setForm((f) => ({
            ...f,
            country: localeDefaults.country,
            currency: localeDefaults.currency,
        }));
    }, [countryManuallySelected, localeDefaults.country, localeDefaults.currency]);

    const dayMap: Record<string, string> = {
        Monday: t("dashboard.dayMonday"),
        Tuesday: t("dashboard.dayTuesday"),
        Wednesday: t("dashboard.dayWednesday"),
        Thursday: t("dashboard.dayThursday"),
        Friday: t("dashboard.dayFriday"),
        Saturday: t("dashboard.daySaturday"),
        Sunday: t("dashboard.daySunday"),
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;
        onSubmit({
            circleName: form.circleName.trim(),
            memberCount: form.memberCount,
            contributionAmount: form.contributionAmount,
            contributionFrequency: form.contributionFrequency,
            country: form.country,
            currency: form.currency,
            contributionDays: form.contributionDays,
            startDate: form.startDate,
        });
    };

    return (
        <SandboxSectionCard>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "20px",
                }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={onCancel}
                    size="small"
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        color: "#6c757d",
                        textTransform: "none",
                    }}>
                    {t("common:buttons.back")}
                </Button>
                <SandboxSectionTitle sx={{ margin: 0 }}>
                    {t("demo.formTitle")}
                </SandboxSectionTitle>
            </Box>

            <SandboxDisclaimer>
                <Info
                    sx={{ color: colors.mainSecondary, fontSize: 18, marginTop: "1px" }}
                />
                <Typography
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: "0.82rem",
                        color: "#4a5568",
                        lineHeight: 1.6,
                    }}>
                    {t("demo.formDisclaimer")}
                </Typography>
            </SandboxDisclaimer>

            <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    {/* Circle name */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label={t("demo.fieldCircleName")}
                            value={form.circleName}
                            onChange={(e) =>
                                setForm((f) => ({ ...f, circleName: e.target.value }))
                            }
                            error={!!errors.circleName}
                            helperText={errors.circleName}
                            inputProps={{ maxLength: 60 }}
                            sx={fieldSx}
                        />
                    </Grid>

                    {/* Start date */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label={t("demo.fieldStartDate")}
                            type="date"
                            value={form.startDate}
                            onChange={(e) =>
                                setForm((f) => ({ ...f, startDate: e.target.value }))
                            }
                            error={!!errors.startDate}
                            helperText={errors.startDate}
                            InputLabelProps={{ shrink: true }}
                            sx={fieldSx}
                        />
                    </Grid>

                    {/* Country */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth sx={fieldSx}>
                            <InputLabel id="sandbox-country-label">{t("demo.fieldCountry")}</InputLabel>
                            <Select
                                labelId="sandbox-country-label"
                                label={t("demo.fieldCountry")}
                                value={form.country}
                                onChange={(e) => handleCountryChange(e.target.value)}
                                MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}>
                                {[...SUPPORTED_COUNTRIES]
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map((c) => (
                                        <MenuItem key={c.code} value={c.code} sx={menuItemSx}>
                                            {c.name}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Currency (auto-set, read-only chip) */}
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: "flex", alignItems: "center", height: "100%", paddingTop: "8px" }}>
                            <Chip
                                label={t("demo.fieldCurrency", { currency: form.currency.toUpperCase() })}
                                variant="outlined"
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 700,
                                    fontSize: "0.875rem",
                                    borderColor: colors.mainSecondary,
                                    color: colors.primary,
                                    padding: "4px 8px",
                                }}
                            />
                            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.75rem", color: "#6c757d", marginLeft: "10px" }}>
                                {t("demo.fieldCurrencyAuto")}
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Member count */}
                    <Grid item xs={12} sm={6}>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: "0.85rem",
                                fontWeight: 600,
                                color: colors.primary,
                                marginBottom: "8px",
                            }}>
                            {t("demo.fieldMembers", { count: form.memberCount })}
                        </Typography>
                        <Slider
                            value={form.memberCount}
                            min={2}
                            max={20}
                            step={1}
                            marks={[
                                { value: 2, label: "2" },
                                { value: 10, label: "10" },
                                { value: 20, label: "20" },
                            ]}
                            onChange={(_, v) =>
                                setForm((f) => ({ ...f, memberCount: v as number }))
                            }
                            sx={{
                                color: colors.mainSecondary,
                                "& .MuiSlider-markLabel": {
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: "0.72rem",
                                },
                            }}
                        />
                        {errors.memberCount && (
                            <Typography sx={{ color: "#cc0000", fontSize: "0.75rem", fontFamily: "'Montserrat', sans-serif" }}>
                                {errors.memberCount}
                            </Typography>
                        )}
                    </Grid>

                    {/* Contribution amount */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label={t("demo.fieldAmount")}
                            type="number"
                            value={form.contributionAmount}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    contributionAmount: Number(e.target.value),
                                }))
                            }
                            error={!!errors.contributionAmount}
                            helperText={
                                errors.contributionAmount ||
                                t("demo.fieldAmountHelper", {
                                    currency: form.currency.toUpperCase(),
                                    amount: (form.contributionAmount * form.memberCount).toLocaleString(),
                                })
                            }
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">{form.currency.toUpperCase()}</InputAdornment>
                                ),
                            }}
                            inputProps={{ min: 10, max: 10000 }}
                            sx={fieldSx}
                        />
                    </Grid>

                    {/* Contribution period (frequency) */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            select
                            label={t("demo.fieldFrequency")}
                            value={form.contributionFrequency}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    contributionFrequency: e.target.value as "Weekly" | "Bi-Weekly",
                                }))
                            }
                            sx={fieldSx}>
                            {(["Weekly", "Bi-Weekly"] as const).map((opt) => (
                                <MenuItem key={opt} value={opt} sx={menuItemSx}>
                                    {opt === "Weekly" ? t("dashboard.freqWeekly") : t("dashboard.freqBiWeekly")}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Contribution days (multi-select) */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth sx={fieldSx} error={!!errors.contributionDays}>
                            <InputLabel id="sandbox-contribution-days-label">{t("demo.fieldDays")}</InputLabel>
                            <Select
                                labelId="sandbox-contribution-days-label"
                                label={t("demo.fieldDays")}
                                multiple
                                value={form.contributionDays}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        contributionDays: e.target.value as string[],
                                    }))
                                }
                                input={<OutlinedInput label={t("demo.fieldDays")} />}
                                renderValue={(selected) =>
                                    (selected as string[])
                                        .map((day) => dayMap[day] ?? day)
                                        .join(", ")
                                }
                                MenuProps={{ PaperProps: { sx: { maxHeight: 260 } } }}>
                                {ALL_DAYS.map((day) => (
                                    <MenuItem key={day} value={day} sx={menuItemSx}>
                                        <Checkbox
                                            checked={form.contributionDays.includes(day)}
                                            size="small"
                                            sx={{ color: colors.mainSecondary, "&.Mui-checked": { color: colors.mainSecondary } }}
                                        />
                                        <ListItemText
                                            primary={dayMap[day] ?? day}
                                            primaryTypographyProps={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.875rem" }}
                                        />
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.contributionDays && (
                                <Typography sx={{ color: "#cc0000", fontSize: "0.75rem", fontFamily: "'Montserrat', sans-serif", marginTop: "4px" }}>
                                    {errors.contributionDays}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>


                </Grid>

                <Box sx={{ display: "flex", gap: "12px", marginTop: "28px", flexWrap: "wrap" }}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isLoading}
                        startIcon={
                            isLoading ? <CircularProgress size={16} /> : <PlayArrow />
                        }
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                            fontSize: "0.95rem",
                            padding: "12px 28px",
                            borderRadius: "50px",
                            backgroundColor: colors.mainSecondary,
                            textTransform: "none",
                            boxShadow: "0 4px 16px rgba(30,193,149,0.3)",
                            "&:hover": { backgroundColor: "#17a37d" },
                        }}>
                        {isLoading ? t("demo.submitLoading") : t("demo.submitButton")}
                    </Button>
                </Box>
            </Box>
        </SandboxSectionCard>
    );
}

// ── Shared field MUI sx helpers ───────────────────────────────────────────
const fieldSx = {
    "& .MuiInputLabel-root": {
        fontFamily: "'Montserrat', sans-serif",
        fontSize: "0.875rem",
    },
    "& .MuiInputBase-input": {
        fontFamily: "'Montserrat', sans-serif",
        fontSize: "0.875rem",
    },
    "& .MuiFormHelperText-root": {
        fontFamily: "'Montserrat', sans-serif",
        fontSize: "0.75rem",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.mainSecondary,
    },
    "& .MuiInputLabel-root.Mui-focused": { color: colors.mainSecondary },
};

const menuItemSx = {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.875rem",
};

// ── Landing / choice screen ───────────────────────────────────────────────
function DemoLanding({
    onQuickDemo,
    onCustomize,
}: {
    onQuickDemo: () => void;
    onCustomize: () => void;
}) {
    const { t } = useTranslation("sandbox");
    return (
        <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
                <SandboxSectionCard
                    sx={{
                        cursor: "pointer",
                        border: `2px solid ${colors.mainSecondary}`,
                        transition: "all 0.2s ease",
                        "&:hover": {
                            boxShadow: "0 8px 24px rgba(30,193,149,0.18)",
                            transform: "translateY(-4px)",
                        },
                    }}
                    onClick={onQuickDemo}>
                    <Box
                        sx={{
                            width: "52px",
                            height: "52px",
                            borderRadius: "12px",
                            background: colors.mainSecondary,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "16px",
                        }}>
                        <PlayArrow sx={{ color: "#fff", fontSize: 28 }} />
                    </Box>
                    <SandboxSectionTitle sx={{ marginBottom: "8px" }}>
                        {t("demo.quickDemoTitle")}
                    </SandboxSectionTitle>
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.9rem",
                            color: "#6c757d",
                            lineHeight: 1.7,
                        }}>
                        {t("demo.quickDemoDesc")}
                    </Typography>
                </SandboxSectionCard>
            </Grid>

            <Grid item xs={12} sm={6}>
                <SandboxSectionCard
                    sx={{
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                            boxShadow: "0 8px 24px rgba(4,13,69,0.12)",
                            transform: "translateY(-4px)",
                        },
                    }}
                    onClick={onCustomize}>
                    <Box
                        sx={{
                            width: "52px",
                            height: "52px",
                            borderRadius: "12px",
                            background: colors.primary,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "16px",
                        }}>
                        <TuneOutlined sx={{ color: "#fff", fontSize: 26 }} />
                    </Box>
                    <SandboxSectionTitle sx={{ marginBottom: "8px" }}>
                        {t("demo.customDemoTitle")}
                    </SandboxSectionTitle>
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.9rem",
                            color: "#6c757d",
                            lineHeight: 1.7,
                        }}>
                        {t("demo.customDemoDesc")}
                    </Typography>
                </SandboxSectionCard>
            </Grid>
        </Grid>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────
const SandboxDemoPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const circle = useSelector(selectSandboxCircle);
    const isLoading = useSelector(selectSandboxIsLoading);
    const error = useSelector(selectSandboxError);
    const { t } = useTranslation("sandbox");

    const [mode, setMode] = useState<DemoMode>("idle");

    // If Redux already holds a circle from a previous visit within the session,
    // go straight to the dashboard.
    useEffect(() => {
        if (circle && circle.sandboxStatus === "active") {
            setMode("ready");
        }
    }, [circle]);

    const handleQuickDemo = () => {
        const { country, currency } = getLocaleDefaults(i18n.resolvedLanguage || i18n.language);
        setMode("loading");
        dispatch(createQuickDemoCircle({ country, currency }));
    };

    const handleCustomSubmit = (payload: ICreateSandboxCirclePayload) => {
        dispatch(createCustomSandboxCircle(payload));
    };

    const handleReset = () => {
        dispatch(clearSandboxState());
        setMode("idle");
    };

    // When the circle arrives from Redux, transition to ready
    useEffect(() => {
        if (circle && isLoading === false) {
            setMode("ready");
        }
    }, [circle, isLoading]);

    return (
        <SandboxPageWrapper>
            {/* ── Hero ── */}
            <SandboxHeroBanner>
                <SandboxBadge label={t("demo.badge")} />
                <SandboxHeroTitle>
                    {t("demo.heroTitle")}
                </SandboxHeroTitle>
                <SandboxHeroSubtitle>
                    {t("demo.heroSubtitle")}
                </SandboxHeroSubtitle>
            </SandboxHeroBanner>

            <SandboxContentArea>
                {/* ── Error state ── */}
                {error && mode !== "ready" && (
                    <Alert
                        severity="error"
                        sx={{ marginBottom: "24px", fontFamily: "'Montserrat', sans-serif" }}
                        onClose={handleReset}>
                        {error}
                    </Alert>
                )}

                {/* ── Loading spinner (initial create) ── */}
                {(mode === "loading" || (isLoading && mode !== "custom-form")) && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            padding: "60px 0",
                            gap: "16px",
                        }}>
                        <CircularProgress sx={{ color: colors.mainSecondary }} />
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: "0.95rem",
                                color: "#6c757d",
                            }}>
                            {t("demo.buildingCircle")}
                        </Typography>
                    </Box>
                )}

                {/* ── Idle: show choice landing ── */}
                {mode === "idle" && !isLoading && (
                    <DemoLanding
                        onQuickDemo={handleQuickDemo}
                        onCustomize={() => setMode("custom-form")}
                    />
                )}

                {/* ── Custom form ── */}
                {mode === "custom-form" && !isLoading && !circle && (
                    <CustomSandboxForm
                        onSubmit={handleCustomSubmit}
                        onCancel={() => setMode("idle")}
                        isLoading={isLoading}
                    />
                )}

                {/* ── Custom form loading state ── */}
                {mode === "custom-form" && isLoading && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            padding: "60px 0",
                            gap: "16px",
                        }}>
                        <CircularProgress sx={{ color: colors.mainSecondary }} />
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: "0.95rem",
                                color: "#6c757d",
                            }}>
                            {t("demo.buildingCustomCircle")}
                        </Typography>
                    </Box>
                )}

                {/* ── Dashboard ── */}
                {mode === "ready" && circle && !isLoading && (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                marginBottom: "16px",
                            }}>
                            <Button
                                startIcon={<ArrowBack />}
                                onClick={handleReset}
                                size="small"
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 600,
                                    fontSize: "0.8rem",
                                    color: "#6c757d",
                                    textTransform: "none",
                                }}>
                                {t("demo.tryDifferent")}
                            </Button>
                        </Box>
                        <SandboxDashboard isReadOnly={false} />
                    </>
                )}
            </SandboxContentArea>
        </SandboxPageWrapper>
    );
};

export default SandboxDemoPage;
